import { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/core';
import FeverBadgeButton from 'components/Button/FeverBadgeButton';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import MusicOffIcon from '@material-ui/icons/MusicOff';
// import { Typography } from '@material-ui/core';
import useGetEntryRecordResult from 'client/hooks/useGetEntryRecordResult';
import { system900 } from 'Mui/styles/color';
import { isMobile } from 'react-device-detect';

// const AUDIO_URL = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';
// const AUDIO_URL =
//     'https://assets.fevercdn.com/ai/dev_wc/promotions/54201657-c23f-4053-beab-1bc3d7ba0664/audios/ai_generated_ec821cfb-646d-4f82-a7f4-216c3b876de3_1751369798.wav';
const STORAGE_KEY = 'hasPlayedAudio';

function AudioPlayerButton({ isLoading }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const { getEntryRecordResult } = useGetEntryRecordResult();
    const [audioUrl, setAudioUrl] = useState('');
    const audioRef = useRef(null);
    const [isButtonClicked, setIsButtonClicked] = useState(false);
    const fadeIntervalRef = useRef(null);

    useEffect(() => {
        const init = async () => {
            const { generativeAudioExtension } = await getEntryRecordResult();
            if (generativeAudioExtension) {
                setAudioUrl(generativeAudioExtension);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 簡化：只需要知道是否顯示按鈕
    const [canShowAudioPlayer, setCanShowAudioPlayer] = useState(false);

    // 初始化音頻
    useEffect(() => {
        if (!audioUrl) return;

        // 清理之前的音頻實例
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
            audioRef.current = null;
        }

        const audio = new Audio(audioUrl);
        audio.loop = true;
        audio.volume = 0;
        audioRef.current = audio;
    }, [audioUrl]);

    // 組件卸載時清理音頻
    useEffect(() => {
        return () => {
            if (fadeIntervalRef.current) {
                clearInterval(fadeIntervalRef.current);
            }
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
                audioRef.current = null;
            }
        };
    }, []);

    // 淡入播放函數
    const fadeInPlay = () => {
        const audio = audioRef.current;
        if (!audio || isPlaying) return;

        // 清除之前的淡入計時器
        if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
        }

        // 設定初始音量為 0
        audio.volume = 0;
        // 開始播放
        audio
            .play()
            .then(() => {
                setIsPlaying(true);                // 3秒淡入效果
                const fadeInDuration = 3000;
                const fadeSteps = 60;
                const volumeStep = 1 / fadeSteps;
                const stepDuration = fadeInDuration / fadeSteps;

                let currentStep = 0;

                fadeIntervalRef.current = setInterval(() => {
                    currentStep++;
                    audio.volume = Math.min(currentStep * volumeStep, 1);

                    if (currentStep >= fadeSteps) {
                        clearInterval(fadeIntervalRef.current);
                        fadeIntervalRef.current = null;
                    }
                }, stepDuration);
            })
            .catch(err => {
                console.log('Play failed:', err);
            });
    };

    // 處理第一次用戶互動
    useEffect(() => {
        let hasInitializedInteraction = false;

        const handleFirstInteraction = async event => {
            // 如果點擊的是按鈕本身，忽略這次事件
            if (event.target.closest('[data-audio-player-button]')) {
                return;
            }

            // 如果剛點擊過按鈕，忽略這次事件
            if (isButtonClicked) {
                setIsButtonClicked(false);
                return;
            }

            // 確保只執行一次初始化
            if (hasInitializedInteraction) {
                return;
            }
            hasInitializedInteraction = true;

            setCanShowAudioPlayer(true);

            // 檢查是否已經播放過
            const stored = localStorage.getItem(STORAGE_KEY);

            if (stored === 'true') {
                // 如果之前播放過，且是點擊或觸控事件，嘗試播放
                if (event.type === 'click' || event.type === 'touchstart') {
                    fadeInPlay();
                }
            } else {
                // 第一次使用，記錄到 localStorage
                localStorage.setItem(STORAGE_KEY, 'true');
            }
        };

        // 監聽用戶互動事件
        const events = [
            'click',
            'touchstart',
            'keydown',
            'scroll',
            'wheel',
            'drag',
            'drop',
            'focus',
            'blur',
            'input',
            'change',
            'submit'
        ];

        events.forEach(event => {
            document.addEventListener(event, handleFirstInteraction, { once: true });
        });

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleFirstInteraction);
            });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let hasInitializedInteraction = false;
        if (!audioRef.current || !isMobile) return;

        const handleGlobalClick = () => {
            //如果剛點擊過按鈕，忽略這次全域點擊
            if (isButtonClicked) {
                setIsButtonClicked(false);
                return;
            }
            if (hasInitializedInteraction) {
                return;
            }

            const audio = audioRef.current;
            if (!audio || isPlaying) return;

            setCanShowAudioPlayer(true);
            // 檢查是否已經播放過
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === 'true') {
                // 如果之前播放過，使用淡入播放
                fadeInPlay();
            } else {
                // 第一次播放，使用淡入播放並記錄到 localStorage
                fadeInPlay();
                localStorage.setItem(STORAGE_KEY, 'true');
            }
            hasInitializedInteraction = true;
        };

        // 監聽整個 document 的點擊事件
        document.addEventListener('click', handleGlobalClick);

        return () => {
            document.removeEventListener('click', handleGlobalClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);

    // 按鈕點擊處理
    const handlePlayAudio = () => {
        const audio = audioRef.current;
        if (!audio) return;

        setIsButtonClicked(true);

        if (!isPlaying) {
            // 開始播放
            fadeInPlay();
            localStorage.setItem(STORAGE_KEY, 'true');
        } else {
            // 暫停播放
            if (fadeIntervalRef.current) {
                clearInterval(fadeIntervalRef.current);
                fadeIntervalRef.current = null;
            }
            audio.pause();
            setIsPlaying(false);
        }
    };

    return (
        Boolean(canShowAudioPlayer) &&
        Boolean(audioUrl) && (
            <div data-audio-player-button="true">
                <FeverBadgeButton
                    size={40}
                    onClick={handlePlayAudio}
                    disabled={isLoading}
                    Icon={isPlaying ? MusicNoteIcon : MusicOffIcon}
                    customIconContainerStyle={css`
                        border-radius: 50px 0px 0px 50px;
                        width: 28px;
                        height: 28px;
                        padding: 4px;
                    `}
                    customContainerStyle={css`
                        position: absolute;
                        right: 0px;
                        top: 4px;
                        z-index: 30;
                    `}
                    buttonSyle={{
                        hoverBackground: 'rgba(255, 255, 255, 0.8)',
                        background: 'rgba(255, 255, 255, 0.8)',
                        icon: system900.color
                    }}
                />
            </div>
        )
    );
}

export default AudioPlayerButton;
